import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { StructuredData } from '../components/StructuredData';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const FrameworkPage = () => {
  const navigate = useNavigate();
  const [isHeaderVisible, setIsHeaderVisible] = useState(false);
  const { isReady, registerRef, visibleSections } = useScrollAnimation(0.1, 100);

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
    // Trigger header animation
    const timer = setTimeout(() => setIsHeaderVisible(true), 300);
    return () => clearTimeout(timer);
  }, []);

  const structuredData = useMemo(() => ({
    "name": "Investment Framework - Our 3-Step Process",
    "description": "Discover Savi Investing's systematic approach to stock selection, including business evaluation, financial analysis, and valuation methodology.",
    "url": "https://saviinvesting.in/framework"
  }), []);

  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title="How We Invest | Savi Investing"
        description="The process we follow while examining a business inside out, is a journey in itself. One where quality and numbers combine to tell a story."
      />
      <StructuredData 
        type="WebPage"
        data={structuredData}
      />
      <Header />
      
      <main className="flex-grow pt-24">
        {/* Page Header */}
        <section className="bg-[#29367A] text-white py-24">
          <div className="w-full max-w-7xl mx-auto px-8">
            <h1 
              className={`text-5xl font-bold text-center transition-all duration-1000 transform ${
                isHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              Our Investing Framework
            </h1>
          </div>
        </section>

        {/* Introduction */}
        <section className="py-10 bg-white">
          <div className="w-full max-w-7xl mx-auto px-8">
            <p className="text-lg text-gray-700 leading-relaxed text-center max-w-4xl mx-auto">
              Savi, in its scrutiny of stocks, looks for a healthy combination of essentially 3 things, before ascertaining where, and how much, to invest.
            </p>
          </div>
        </section>

        {/* Step 1: Quality Business - Brand Color Background */}
        <section
          id="step1"
          ref={registerRef('step1')}
          data-section="step1"
          className="py-24 bg-[#29367A]"
        >
          <div className="w-full max-w-7xl mx-auto px-8">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              {/* Text Content - Left */}
              <div
                className={`transition-all duration-1000 transform ${
                  visibleSections.step1 ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
                }`}
              >
                <h2 className="text-4xl font-bold text-white mb-6">1. Quality Business</h2>
                
                <p className="text-lg text-white leading-relaxed mb-4">
                  We examine the company's core business segments/models for their soundness and relevance. We then see what the company's competitive advantage is in its specific business(s), and the longevity of this very advantage.
                </p>
                
                <p className="text-lg text-white leading-relaxed">
                  We do get attracted to 'economic moats' - companies with minimal competition and minimal threat of disruption, and companies with management that have a track record of good and clean practices.
                </p>
              </div>

              {/* Image - Right */}
              <div
                className={`transition-all duration-1000 delay-200 transform ${
                  visibleSections.step1 ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
                }`}
              >
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src="https://images.unsplash.com/39/lIZrwvbeRuuzqOoWJUEn_Photoaday_CSD%20%281%20of%201%29-5.jpg"
                    alt="Quality Business"
                    className="w-full h-[400px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Step 2: Financial Health - White Background */}
        <section
          id="step2"
          ref={registerRef('step2')}
          data-section="step2"
          className="py-24 bg-white"
        >
          <div className="w-full max-w-7xl mx-auto px-8">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              {/* Image - Left */}
              <div
                className={`transition-all duration-1000 transform ${
                  visibleSections.step2 ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
                }`}
              >
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src="https://images.unsplash.com/photo-1618044733300-9472054094ee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxiYWxhbmNlJTIwc2hlZXR8ZW58MHx8fHwxNzc1NDA4OTY1fDA&ixlib=rb-4.1.0&q=85"
                    alt="Financial Health and Valuation"
                    className="w-full h-[400px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#29367A]/30 to-transparent"></div>
                </div>
              </div>

              {/* Text Content - Right */}
              <div
                className={`transition-all duration-1000 delay-200 transform ${
                  visibleSections.step2 ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
                }`}
              >
                <h2 className="text-4xl font-bold text-gray-900 mb-6">2. Strong financial health and compelling valuation</h2>
                
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  Here's where we get down to numbers, quantifying whatever that can be quantified. We screen the company's balance sheet and profit & loss statement, on specifically 10 parameters (8 quantitative and 2 qualitative).
                </p>
                
                <p className="text-lg text-gray-700 leading-relaxed">
                  While the company's score in these 10 areas does not conclude things for us, we do get attracted to the ones that fare 7 or more, and/or have a price-to-earnings multiple of less than 10, and/or a price-to-book multiple of less than 1.5.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Step 3: Growth in Earnings - Brand Color Background */}
        <section
          id="step3"
          ref={registerRef('step3')}
          data-section="step3"
          className="py-24 bg-[#29367A]"
        >
          <div className="w-full max-w-7xl mx-auto px-8">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              {/* Text Content - Left */}
              <div
                className={`transition-all duration-1000 transform ${
                  visibleSections.step3 ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
                }`}
              >
                <h2 className="text-4xl font-bold text-white mb-6">3. Growth in Earnings</h2>
                
                <p className="text-lg text-white leading-relaxed mb-4">
                  We then chart the company's roadmap for the future, and estimate if it can, at least on a standalone (core business) basis, clock a Profit-after-Tax CAGR of 25% or more over the next 5 years, or, if it has clocked a PAT CAGR of 25% or more in the preceding 3 years.
                </p>
                
                <p className="text-lg text-white leading-relaxed">
                  Using the company's historical Price-to-Earnings multiple as the base, we establish how close, or how far, the company's stock price is from our desired target.
                </p>
              </div>

              {/* Image - Right */}
              <div
                className={`transition-all duration-1000 delay-200 transform ${
                  visibleSections.step3 ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
                }`}
              >
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src="https://images.unsplash.com/photo-1730382624709-81e52dd294d4"
                    alt="Growth in Earnings"
                    className="w-full h-[400px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Conclusion */}
        <section className="py-10 bg-white">
          <div className="w-full max-w-7xl mx-auto px-8">
            <p className="text-lg text-gray-700 leading-relaxed text-center max-w-4xl mx-auto mb-8">
              In the end, hardly any company or business will entail all of the above 3 elements in an even fashion. But, we manage to find some that emerge outliers in any one of the three, while being fairly consistent in the other two.
            </p>

            {/* CTA Button */}
            <div className="flex justify-center">
              <Button
                onClick={() => navigate('/reach-us')}
                className="bg-[#29367A] hover:bg-[#1f2859] text-white px-8 py-6 text-base font-semibold rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Get in touch with us
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default FrameworkPage;
